import { effectiveRoute } from "@talentkontor/domain";
import { ApiError, type Api, type Filter } from "../../../data/api";
import { DEAL_STATUS, monateDerKooperation, monthText, type MonatInfo, type Vorschlag } from "./model";
import type { MonatsPlan, Speicherdaten } from "./formular";
import { ladeBeitraegeDerKooperation } from "./beitraege-daten";
import type {
  ArticleLite, AttachmentLite, BonusRow, BookingLite, BrandRow, CampaignRow, CatalogEntry, ContractLinkRow, ContractLite, DealFull, DealStatus, ExklusivDeal,
  HistoryRow, InvoicePeriodRow, InvoiceRow, ItemRow, KoopDaten, KoopDetail, MemberLite, MessageLite, PeriodFull, RevisionTerms, SettingsLite, StatusLabel,
  SubmissionRow, Substep, TalentRow, TermsRow,
} from "./typen";
import { inLang, t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege der Kooperationen. Sie bekommen den Datenanschluss übergeben, damit dieselben Funktionen in der App
 * (Supabase oder Demo) und in den Tests gegen die Demo-Datenbank laufen. Rechte prüft immer die Datenbank; wo RLS eine Änderung
 * still ins Leere laufen lässt (0 Zeilen), melden die Schreibwege das ausdrücklich.
 */

const SETTINGS_COLUMNS =
  "org_id, legal_name, street, postal_code, city, country, vat_id, currency, currencies, timezone, default_commission_bp, agency_fee_mode, " +
  "agency_fee_bp, agency_fee_cents, invoice_route, invoice_number_prefix, deal_models_enabled, expense_keywords, locale, requested_urgent_days, " +
  "revenue_start, free_revisions, revision_fee_cents, origin_commission_bp, deliverable_no_approval_formats";
const CONTRACT_COLUMNS = "id, org_id, deal_id, talent_id, brand_id, title, status, contract_number, sent_at, completed_at, created_at, origin, ends_on, auto_renew";
const CATALOG_COLUMNS = "id, org_id, key, label_de, label_en, active, sort_order";
const MESSAGE_COLUMNS = "id, talent_id, sender_user_id, sender_side, body, link_kind, link_id, created_at";

const byOrg = (orgId: string): Filter[] => [["org_id", "eq", orgId]];

/** Nicht lesbar (RLS, fehlende Rechte) ergibt eine leere Liste statt eines Fehlers: interne Daten sind nicht für jede Rolle sichtbar. */
function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch(() => [] as T[]);
}

/** Produkt-Standards (platform_defaults) für Einstellungen, die die Agentur leer gelassen hat (Grundsatz aus 0006). */
const DEFAULT_KEYS = ["deal_models_enabled", "expense_keywords", "requested_urgent_days", "revenue_start", "free_revisions"] as const;
type Standards = Record<(typeof DEFAULT_KEYS)[number], unknown>;
async function ladeStandards(api: Api): Promise<Standards> {
  const rows = await quiet(api.select<{ key: string; value: unknown }>("platform_defaults", { filters: [["key", "in", [...DEFAULT_KEYS]]] }));
  const get = (k: string) => {
    const v = rows.find((r) => r.key === k)?.value;
    return typeof v === "string" ? safeJson(v) ?? v : v;
  };
  return Object.fromEntries(DEFAULT_KEYS.map((k) => [k, get(k)])) as Standards;
}
function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : null);

async function ladeEinstellungen(api: Api, orgId: string): Promise<{ settings: SettingsLite | null; labels: StatusLabel[]; models: string[]; substeps: Substep[] }> {
  const [settings, labels, std, substeps] = await Promise.all([
    quiet(api.select<SettingsLite>("org_settings", { columns: SETTINGS_COLUMNS, filters: byOrg(orgId) })).then((r) => r[0] ?? null),
    quiet(api.select<StatusLabel>("org_status_labels", { columns: "status, label_de, label_en, hidden", filters: byOrg(orgId) })),
    ladeStandards(api),
    // 0394 (K-526): eigene Zwischenstände; fehlt die Tabelle (älterer Stand), einfach keine
    quiet(api.select<Substep>("org_deal_substeps", { columns: "id, parent_status, label_de, label_en, sort, archived", filters: byOrg(orgId), order: [["sort", "asc"], ["label_de", "asc"]] })),
  ]);
  // Leere Einstellungen bekommen den Produkt-Standard: Frist, Umsatzbeginn und Korrekturschleifen rechnen überall gleich
  const eff: SettingsLite | null = settings && {
    ...settings,
    // Leere Liste = Rückfrage aus (SAAS-R3-11); ohne Angabe die Stichworte der Plattform
    expense_keywords: Array.isArray(settings.expense_keywords) ? settings.expense_keywords : strings(std.expense_keywords),
    requested_urgent_days: settings.requested_urgent_days ?? int(std.requested_urgent_days) ?? 14,
    revenue_start: settings.revenue_start ?? (std.revenue_start === "contract_signed" ? "contract_signed" : "status"),
    free_revisions: settings.free_revisions ?? int(std.free_revisions) ?? 2,
  };
  return { settings: eff, labels, models: settings?.deal_models_enabled?.length ? settings.deal_models_enabled : strings(std.deal_models_enabled), substeps };
}

/** Kataloge der Agentur (0047), nach Reihenfolge; abgeschaltete Einträge bleiben für bestehende Kooperationen lesbar. */
async function ladeKataloge(api: Api, orgId: string): Promise<{ platforms: CatalogEntry[]; itemKinds: CatalogEntry[] }> {
  const order = [["sort_order", "asc"], ["label_de", "asc"]] as const;
  const [platforms, itemKinds] = await Promise.all([
    quiet(api.select<CatalogEntry>("org_deal_platforms", { columns: CATALOG_COLUMNS, filters: byOrg(orgId), order })),
    quiet(api.select<CatalogEntry>("org_item_kinds", { columns: `${CATALOG_COLUMNS}, commissionable`, filters: byOrg(orgId), order })),
  ]);
  return { platforms, itemKinds };
}

/** Liste, Board und Ansicht nach Marke: alle Kooperationen der Agentur, die die angemeldete Person sehen darf. */
export async function ladeKooperationen(api: Api, orgId: string): Promise<KoopDaten> {
  const by = byOrg(orgId);
  const [deals, periods, items, terms, invoices, talents, brands, contracts, cfg, campaigns, contractLinks, invoicePeriods, kat] = await Promise.all([
    api.select<DealFull>("deals", { filters: by, order: [["created_at", "desc"]] }),
    api.select<PeriodFull>("deal_periods", { filters: by, order: [["month", "asc"]] }),
    api.select<ItemRow>("deal_line_items", { filters: by }),
    quiet(api.select<TermsRow>("deal_agency_terms", { filters: by })),
    api.select<InvoiceRow>("invoices", { filters: by }),
    api.select<TalentRow>("talents", { filters: by, order: [["first_name", "asc"]] }),
    api.select<BrandRow>("brands", { filters: by, order: [["name", "asc"]] }),
    quiet(api.select<ContractLite>("contracts", { columns: CONTRACT_COLUMNS, filters: by })),
    ladeEinstellungen(api, orgId),
    quiet(api.select<CampaignRow>("campaigns", { filters: by, order: [["created_at", "desc"]] })),
    quiet(api.select<ContractLinkRow>("contract_deal_links", { columns: "id, contract_id, deal_id, created_at", filters: by })),
    quiet(api.select<InvoicePeriodRow>("invoice_periods", { columns: "invoice_id, period_id", filters: by })),
    ladeKataloge(api, orgId),
  ]);
  const brandById = new Map(brands.map((b) => [b.id, b]));
  const withRoute = deals.map((d) => ({ ...d, route: effectiveRoute(d.invoice_route, brandById.get(d.brand_id)?.invoice_route, cfg.settings?.invoice_route) }));
  return {
    deals: withRoute, periods, items, terms, invoices, talents, brands, contracts, settings: cfg.settings, labels: cfg.labels, models: cfg.models, substeps: cfg.substeps,
    campaigns, contractLinks, invoicePeriods, ...kat,
  };
}

/** Eine Kooperation mit Monaten, Rechnungen, Verträgen, Nachrichten und Buchungen (Detailseite, Content-Prüfung, Bearbeiten). */
export async function ladeKooperation(api: Api, orgId: string, dealId: string, opts: { auszahlungLesbar?: boolean } = {}): Promise<KoopDetail | null> {
  const deal = (await api.select<DealFull>("deals", { filters: [["id", "eq", dealId], ["org_id", "eq", orgId]] }))[0];
  if (!deal) return null;
  const byDeal: Filter[] = [["deal_id", "eq", dealId]];
  let invoicesReadFailed = false;
  const [periods, items, terms, invoicesByDeal, talents, brands, contracts, candidates, messages, bookings, members, articles, links, payout, cfg,
    contractLinks, submissions, history, revisionTerms, campaigns, kat, creatorMessages, bonuses, beitragsDaten] = await Promise.all([
    api.select<PeriodFull>("deal_periods", { filters: byDeal, order: [["month", "asc"]] }),
    api.select<ItemRow>("deal_line_items", { filters: byDeal }),
    quiet(api.select<TermsRow>("deal_agency_terms", { filters: byDeal })),
    api.select<InvoiceRow>("invoices", { filters: byDeal }).catch(() => {
      invoicesReadFailed = true;
      return [] as InvoiceRow[];
    }),
    api.select<TalentRow>("talents", { filters: [["id", "eq", deal.talent_id]] }),
    // Marke und, falls ihre Rechnung über eine vertretende Agentur läuft, auch diese (Rechnungsempfänger)
    api.select<BrandRow>("brands", { filters: [["id", "eq", deal.brand_id]] }).then(async (rows) => {
      const via = rows[0]?.invoice_via_agency && rows[0]?.represented_by ? rows[0].represented_by : null;
      return via ? [...rows, ...(await quiet(api.select<BrandRow>("brands", { filters: [["id", "eq", via]] })))] : rows;
    }),
    quiet(api.select<ContractLite>("contracts", { columns: CONTRACT_COLUMNS, filters: byDeal })),
    // Verknüpfbar: Verträge des Creators und Rahmenverträge der Marke ohne Creator (0047: auch versendete und unterschriebene)
    Promise.all([
      quiet(api.select<ContractLite>("contracts", { columns: CONTRACT_COLUMNS, filters: [["talent_id", "eq", deal.talent_id], ["org_id", "eq", orgId]] })),
      quiet(api.select<ContractLite>("contracts", { columns: CONTRACT_COLUMNS, filters: [["brand_id", "eq", deal.brand_id], ["talent_id", "is", null], ["org_id", "eq", orgId]] })),
    ]).then(([a, b]) => [...a, ...b]),
    quiet(api.select<MessageLite>("messages", {
      columns: MESSAGE_COLUMNS,
      filters: [["talent_id", "eq", deal.talent_id], ["link_kind", "eq", "deal"], ["link_id", "eq", dealId]],
      order: [["created_at", "desc"]],
    })),
    quiet(api.select<BookingLite>("bookings", { columns: "id, deal_id, period_id, kind, booked_on, amount_cents, currency, status, note", filters: byDeal, order: [["booked_on", "desc"]] })),
    quiet(api.select<MemberLite>("memberships", { columns: "user_id, display_name", filters: byOrg(orgId) })),
    quiet(api.select<ArticleLite>("invoice_articles", { columns: "id, name, description, default_price_cents, currency, commissionable", filters: [["org_id", "eq", orgId], ["active", "is", true]], order: [["sort_order", "asc"], ["name", "asc"]] })),
    quiet(api.select<{ talent_id: string }>("talent_user_links", { columns: "talent_id", filters: [["talent_id", "eq", deal.talent_id]] })),
    opts.auszahlungLesbar ? quiet(api.select<{ iban: string | null }>("talent_payout_details", { columns: "iban", filters: [["talent_id", "eq", deal.talent_id]] })) : Promise.resolve(null),
    ladeEinstellungen(api, orgId),
    quiet(api.select<ContractLinkRow>("contract_deal_links", { columns: "id, contract_id, deal_id, created_at", filters: byDeal })),
    quiet(api.select<SubmissionRow>("deal_content_submissions", { filters: byDeal, order: [["version", "desc"]] })),
    api.read<HistoryRow[] | null>("deal_history", { p_deal: dealId }).then((r) => r ?? []).catch(() => [] as HistoryRow[]),
    api.read<RevisionTerms | null>("deal_revision_terms", { p_deal: dealId }).catch(() => null),
    quiet(api.select<CampaignRow>("campaigns", { filters: [["org_id", "eq", orgId], ["brand_id", "eq", deal.brand_id]], order: [["created_at", "desc"]] })),
    ladeKataloge(api, orgId),
    // Dateien für die Content-Prüfung: nur, was der Creator im Chat geschickt hat (auch ohne Verweis auf die Kooperation)
    quiet(api.select<MessageLite>("messages", {
      columns: MESSAGE_COLUMNS, filters: [["talent_id", "eq", deal.talent_id], ["sender_side", "eq", "talent"]], order: [["created_at", "desc"]], limit: 60,
    })),
    // Bonus nach Kennzahl (0060)
    quiet(api.select<BonusRow>("deal_bonus_conditions", { filters: byDeal, order: [["created_at", "asc"]] })),
    // Beiträge und Nachweise der Kennzeichnung (0068)
    ladeBeitraegeDerKooperation(api, dealId),
  ]);
  // Rechnungen, die nur über den Monat an der Kooperation hängen: direkt (period_id) oder als Sammelrechnung (invoice_periods)
  let invoices = invoicesByDeal;
  const ids = periods.map((p) => p.id);
  let invoicePeriods: InvoicePeriodRow[] = [];
  if (ids.length && !invoicesReadFailed) {
    const [viaPeriod, viaLinks] = await Promise.all([
      api.select<InvoiceRow>("invoices", { filters: [["period_id", "in", ids]] }).catch(() => {
        invoicesReadFailed = true;
        return [] as InvoiceRow[];
      }),
      quiet(api.select<InvoicePeriodRow>("invoice_periods", { columns: "invoice_id, period_id", filters: [["period_id", "in", ids]] })),
    ]);
    invoicePeriods = viaLinks;
    const seen = new Set(invoices.map((i) => i.id));
    invoices = [...invoices, ...viaPeriod.filter((i) => !seen.has(i.id))];
    const missing = [...new Set(viaLinks.map((l) => l.invoice_id))].filter((id) => !invoices.some((i) => i.id === id));
    if (missing.length) invoices = [...invoices, ...(await quiet(api.select<InvoiceRow>("invoices", { filters: [["id", "in", missing]] })))];
  }
  // Verträge: Hauptbezug und weitere Verknüpfungen (Rahmenvertrag)
  const linkedIds = contractLinks.map((l) => l.contract_id).filter((id) => !contracts.some((c) => c.id === id));
  const linked = linkedIds.length ? await quiet(api.select<ContractLite>("contracts", { columns: CONTRACT_COLUMNS, filters: [["id", "in", linkedIds]] })) : [];
  const allContracts: ContractLite[] = [...contracts.map((c) => ({ ...c, link: "main" as const })), ...linked.map((c) => ({ ...c, link: "link" as const }))];
  const own = new Set(allContracts.map((c) => c.id));
  // Nur Verträge ohne Marke oder derselben Marke (die Datenbank lehnt andere ab)
  const freeContracts = candidates.filter((c, i, list) => c.status !== "void" && !own.has(c.id) && (!c.brand_id || c.brand_id === deal.brand_id)
    && list.findIndex((x) => x.id === c.id) === i);
  const byMessage = new Map([...messages, ...creatorMessages].map((m) => [m.id, m]));
  const msgIds = [...byMessage.keys()];
  const attachments = msgIds.length
    ? await quiet(api.select<AttachmentLite>("attachments", {
        columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at",
        filters: [["owner_kind", "eq", "chat"], ["owner_id", "in", msgIds]],
        order: [["created_at", "desc"]],
      }))
    : [];
  const routed = { ...deal, route: effectiveRoute(deal.invoice_route, brands[0]?.invoice_route, cfg.settings?.invoice_route) };
  return {
    deals: [routed], periods, items, terms, invoices, talents, brands, contracts: allContracts, settings: cfg.settings, labels: cfg.labels, models: cfg.models, substeps: cfg.substeps,
    campaigns, contractLinks, invoicePeriods, ...kat,
    deal: routed, talent: talents[0], brand: brands[0], freeContracts,
    messages,
    // Nur Dateien des Creators (Befund 12): ein Briefing der Agentur ist keine Einreichung
    attachments: attachments.map((a) => ({ ...a, fromCreator: byMessage.get(a.owner_id)?.sender_side === "talent" })),
    bookings, members, articles,
    hasPortalAccount: links.length > 0,
    iban: payout === null ? undefined : (payout[0]?.iban ?? null),
    invoicesReadFailed,
    submissions, history, revisionTerms, bonuses,
    beitraege: beitragsDaten.beitraege, nachweise: beitragsDaten.nachweise,
  };
}

/** Formulardaten: dazu die Kooperationen der Agentur mit Zeitraum für die Exklusivitätsprüfung (Befund 9). */
export interface FormularDaten extends Pick<KoopDaten, "talents" | "brands" | "settings" | "labels" | "models" | "campaigns" | "platforms" | "itemKinds" | "substeps"> {
  exklusiv: ExklusivDeal[];
  exklusivMonate: { deal_id: string; month: string; status: string }[];
}

/** Formular „Kooperation anlegen“: Creator, Marken, Einstellungen, freigeschaltete Modelle, Kampagnen, Kataloge und Kooperationen für die Exklusivität. */
export async function ladeFormularDaten(api: Api, orgId: string): Promise<FormularDaten> {
  const [talents, brands, cfg, campaigns, kat, exklusiv, exklusivMonate] = await Promise.all([
    api.select<TalentRow>("talents", { filters: byOrg(orgId), order: [["first_name", "asc"]] }),
    api.select<BrandRow>("brands", { filters: byOrg(orgId), order: [["name", "asc"]] }),
    ladeEinstellungen(api, orgId),
    quiet(api.select<CampaignRow>("campaigns", { filters: byOrg(orgId), order: [["created_at", "desc"]] })),
    ladeKataloge(api, orgId),
    quiet(api.select<ExklusivDeal>("deals", {
      columns: "id, talent_id, brand_id, title, status, archived_at, exclusive, exclusive_until, exclusive_scope, start_month, months", filters: byOrg(orgId),
    })),
    quiet(api.select<{ deal_id: string; month: string; status: string }>("deal_periods", { columns: "deal_id, month, status", filters: byOrg(orgId) })),
  ]);
  return { talents, brands, settings: cfg.settings, labels: cfg.labels, models: cfg.models, substeps: cfg.substeps, campaigns, ...kat, exklusiv, exklusivMonate };
}

// ── Schreibwege ────────────────────────────────────────────────────────────

function denied(what: string): ApiError {
  return new ApiError(t(`${what} nicht gefunden oder keine Berechtigung.`, `${what} not found or no permission.`), { code: "42501" });
}
function one<T>(rows: T[], what: string): T {
  const r = rows[0];
  if (!r) throw denied(what);
  return r;
}

/** Monate des Bestands mit Rechnungen (für Schutz und Abgleich). */
export function bestandMonate(detail: KoopDetail, timeZone: string): MonatInfo[] {
  return monateDerKooperation(detail.deal, detail.periods, detail.invoices, timeZone, detail.invoicePeriods, detail.terms[0]).monate;
}

/** Ergebnis von deal_save: was die Datenbank mit den Monaten gemacht hat. */
export interface SpeicherErgebnis {
  deal_id: string;
  created: boolean;
  moved: { from: string; to: string } | null;
  added: string[];
  removed: { id: string; month: string }[];
  kept: { id: string; month: string; reason: string }[];
  frozen: { id: string; month: string }[];
  updated_at: string;
  /** Mehrere Creator: alle angelegten Kooperationen */
  deal_ids?: string[];
}

/**
 * Kooperation anlegen oder ändern (AD-051 … AD-064) in einer Transaktion (deal_save, 0047): Marke und Kampagne (falls neu),
 * Kooperation, Konditionen, Monate mit Schutz auf dem Server (KA-06, KA-07), Zusatzpositionen. Bricht ein Schritt ab, bleibt
 * nichts stehen. Hat jemand die Kooperation inzwischen geändert, lehnt die Datenbank ab (Zeitstempel des geladenen Stands).
 */
export async function speichereKooperation(api: Api, ctx: { orgId: string; userId: string | null; timeZone: string; bestand: KoopDetail | null }, daten: Speicherdaten): Promise<{ dealId: string; plan: MonatsPlan; ergebnis: SpeicherErgebnis }> {
  // Mehrere Creator in einem Schritt (Kampagne): je Creator eine Kooperation, eine Transaktion (0060)
  if (!ctx.bestand && daten.weitereCreator?.length) {
    // Mit Herkunft (0074): deal_save_multi, danach Herkunft und ihr Satz
    const r = await api.rpc<{ deal_ids: string[] }>("deal_save_multi_origin", {
      p_origin: daten.deal.origin ?? null,
      p_talents: [String(daten.deal.talent_id), ...daten.weitereCreator],
      p_values: { ...daten.deal, org_id: ctx.orgId },
      p_terms: daten.terms,
      p_months: daten.monate.map((m) => ({ month: m.month, amount_cents: m.amount_cents, commission_forecast_cents: m.commission_forecast_cents, content_deadline: m.content_deadline,
        goods_value_cents: m.goods_value_cents ?? null })),
      p_items: daten.positionen.map((p) => ({ kind: p.kind, description: p.description, amount_cents: p.amount_cents, commissionable: p.commissionable, month: p.month })),
      p_new_brand: daten.newBrand,
      p_new_campaign: daten.newCampaign,
    });
    if (!r?.deal_ids?.length) throw denied(t("Kooperation", "Deal"));
    const plan: MonatsPlan = { neu: daten.monate, aendern: [], loeschen: [], behalten: [], festgehalten: [] };
    return { dealId: r.deal_ids[0]!, plan, ergebnis: { deal_id: r.deal_ids[0]!, created: true, moved: null, added: daten.monate.map((m) => m.month), removed: [], kept: [], frozen: [], updated_at: "", deal_ids: r.deal_ids } };
  }
  const r = await api.rpc<SpeicherErgebnis>("deal_save_origin", {
    p_origin: daten.deal.origin ?? null,
    p_deal: ctx.bestand?.deal.id ?? null,
    p_values: { ...daten.deal, org_id: ctx.orgId },
    p_terms: daten.terms,
    p_months: daten.monate.map((m) => ({
      month: m.month, amount_cents: m.amount_cents, commission_forecast_cents: m.commission_forecast_cents, content_deadline: m.content_deadline,
      goods_value_cents: m.goods_value_cents ?? null,
    })),
    p_items: daten.positionen.map((p) => ({ id: p.id, kind: p.kind, description: p.description, amount_cents: p.amount_cents, commissionable: p.commissionable, month: p.month })),
    p_new_brand: daten.newBrand,
    p_new_campaign: daten.newCampaign,
    p_expected_updated_at: ctx.bestand?.deal.updated_at ?? null,
  });
  if (!r?.deal_id) throw denied(t("Kooperation", "Deal"));
  const byMonth = new Map(daten.monate.map((m) => [m.month, m]));
  const plan: MonatsPlan = {
    neu: r.added.map((m) => byMonth.get(m)).filter((m): m is NonNullable<typeof m> => !!m),
    aendern: [],
    loeschen: r.removed,
    behalten: r.kept.map((k) => ({ id: k.id, month: k.month, grund: k.reason })),
    festgehalten: r.frozen,
  };
  return { dealId: r.deal_id, plan, ergebnis: r };
}

/** Stand der Kooperation setzen (AD-044). Bei Kooperationen mit einem Monat zieht der Monatsstand mit. */
export async function setzeKooperationsStatus(api: Api, deal: Pick<DealFull, "id" | "model">, periods: readonly Pick<PeriodFull, "id">[], status: DealStatus): Promise<void> {
  one(await api.update<DealFull>("deals", [["id", "eq", deal.id]], { status }), t("Kooperation", "Deal"));
  if (periods.length === 1) one(await api.update<PeriodFull>("deal_periods", [["id", "eq", periods[0]!.id]], { manual_status: status }), t("Monat", "Month"));
}

/**
 * Arbeitsstand eines Monats setzen (AD-045). Ist es der einzige oder der aktuelle Monat, folgt der Stand der Kooperation,
 * außer sie steht auf einem Endstatus (blockiert, storniert, abgeschlossen).
 */
export async function setzeMonatsstand(api: Api, input: { deal: Pick<DealFull, "id" | "status">; periodId: string; status: DealStatus; auchKooperation: boolean }): Promise<void> {
  one(await api.update<PeriodFull>("deal_periods", [["id", "eq", input.periodId]], { manual_status: input.status }), t("Monat", "Month"));
  // Ein Endstatus des Monats (abgeschlossen, storniert, blockiert) gilt nie für die ganze Kooperation
  if (input.auchKooperation && !DEAL_STATUS[input.deal.status]?.terminal && !DEAL_STATUS[input.status]?.terminal && input.deal.status !== input.status) {
    one(await api.update<DealFull>("deals", [["id", "eq", input.deal.id]], { status: input.status }), t("Kooperation", "Deal"));
  }
}

/** Monat stornieren (optional mit Ausfallhonorar) oder wieder öffnen (0039); Rechnungen des Monats müssen vorher weg sein. */
export async function storniereMonat(api: Api, periodId: string, grund: string, ausfallCents: number | null): Promise<void> {
  await api.rpc("deal_period_cancel", { p_period: periodId, p_reason: grund.trim(), p_fee_cents: ausfallCents });
}
/** Wieder öffnen, auch mit Ausfallhonorar oder Minderung: der vereinbarte Betrag kommt zurück (0060). */
export async function oeffneMonat(api: Api, periodId: string): Promise<void> {
  await api.rpc("deal_period_reopen", { p_period: periodId });
}

export type Ausgang = "cancelled" | "cancellation_fee" | "not_accepted_reduced" | "not_accepted_fee";
/**
 * Ausgang eines Monats (0060): storniert, Ausfallhonorar (Betrag oder Prozent), nicht abgenommen mit Minderung oder
 * Ausfallhonorar in Prozent. Der ursprüngliche Betrag bleibt erhalten.
 */
export async function setzeAusgang(api: Api, periodId: string, input: { ausgang: Ausgang; grund: string; betragCents?: number | null; prozent?: number | null }): Promise<{ amount_cents: number | null }> {
  return api.rpc("deal_period_outcome", { p_period: periodId, p_outcome: input.ausgang, p_reason: input.grund.trim(), p_amount_cents: input.betragCents ?? null, p_percent: input.prozent ?? null });
}

/** Die ganze Kooperation stornieren: offene Monate ab einem Monat, Ausfallhonorar in Prozent je Monat (0060). */
export async function storniereKooperation(api: Api, dealId: string, input: { grund: string; prozent: number | null; abMonat: string | null }): Promise<{ months: { month: string; fee_cents: number | null }[]; kept: string[] }> {
  return api.rpc("deal_cancel", { p_deal: dealId, p_reason: input.grund.trim(), p_fee_percent: input.prozent, p_from: input.abMonat ? `${input.abMonat}-01` : null });
}

/** Leistungsmonat verschieben, für alle Modelle; die Content-Frist wandert mit (0060). */
export async function verschiebeMonat(api: Api, periodId: string, monat: string): Promise<void> {
  await api.rpc("deal_period_move", { p_period: periodId, p_month: `${monat}-01` });
}

/** Nachkauf oder Verlängerung (Nutzungsrechte, Whitelisting …) als eigener Vorgang mit Anforderung und Rechnung (0060). */
export async function erfasseNachkauf(api: Api, dealId: string, input: { monat: string; art: string; beschreibung: string; betragCents: number; provisionspflichtig: boolean; nutzungBis: string | null }): Promise<{ period_id: string; new_period: boolean }> {
  return api.rpc("deal_follow_up", {
    p_deal: dealId, p_month: `${input.monat}-01`, p_kind: input.art, p_description: input.beschreibung.trim(), p_amount_cents: input.betragCents,
    p_commissionable: input.provisionspflichtig, p_usage_rights_until: input.nutzungBis || null, p_title: null,
  });
}

/** Bonus nach Kennzahl vorbereiten, entscheiden, entfernen (0060). */
export async function legeBonusAn(api: Api, dealId: string, input: { beschreibung: string; bedingung: string; betragCents: number; provisionspflichtig: boolean; pruefen: string | null }): Promise<string> {
  return api.rpc<string>("deal_bonus_add", { p_deal: dealId, p_description: input.beschreibung.trim(), p_condition: input.bedingung.trim(), p_amount_cents: input.betragCents,
    p_commissionable: input.provisionspflichtig, p_check_on: input.pruefen || null });
}
export async function entscheideBonus(api: Api, bonusId: string, erfuellt: boolean, monat: string | null): Promise<void> {
  await api.rpc("deal_bonus_resolve", { p_bonus: bonusId, p_met: erfuellt, p_month: monat ? `${monat}-01` : null });
}
export async function entferneBonus(api: Api, bonusId: string): Promise<void> {
  await api.rpc("deal_bonus_remove", { p_bonus: bonusId });
}

/** Notiz und Content-Deadline eines Monats (AD-065). */
export async function speichereMonat(api: Api, periodId: string, patch: { note: string | null; content_deadline: string | null }): Promise<void> {
  one(await api.update<PeriodFull>("deal_periods", [["id", "eq", periodId]], patch), t("Monat", "Month"));
}

/** Rechnung beim Creator anfordern: Briefing und Positionen gehen an den Monat, Glocke und Mail setzt die Datenbank (AD-046 … AD-048). */
export async function fordereRechnungAn(api: Api, periodId: string, note: string, items: readonly Vorschlag[]): Promise<{ again: boolean }> {
  const r = await api.rpc<{ period_id: string; again: boolean }>("deal_request_invoice", { p_period: periodId, p_note: note.trim() || null, p_items: items });
  return { again: !!r?.again };
}

/** Monat von Hand buchen oder die Buchung zurücknehmen (KA-05, nur mit „Zahlungen bestätigen“). */
export async function bucheMonat(api: Api, periodId: string, booked: boolean): Promise<void> {
  await api.rpc("period_set_booked", { p_period: periodId, p_booked: booked });
}

// ── Archivieren mit Prüfliste (Befund 21) ─────────────────────────────────

export interface ArchivPruefung {
  deal_id: string;
  open_months: number;
  open_invoices: number;
  open_requests: number;
  open_tasks: number;
  details: { months: string[]; invoices: { id: string; number: string | null; status: string; kind: string }[]; requests: string[]; tasks: string[] };
}

/** Was vor dem Archivieren noch offen ist: Monate, Rechnungen, Anforderungen beim Creator, Aufgaben. */
export async function pruefeArchiv(api: Api, dealIds: readonly string[]): Promise<ArchivPruefung[]> {
  if (!dealIds.length) return [];
  return (await api.read<ArchivPruefung[] | null>("deal_archive_check", { p_deals: [...dealIds] })) ?? [];
}
export const offenePosten = (p: Pick<ArchivPruefung, "open_months" | "open_invoices" | "open_requests" | "open_tasks">) =>
  p.open_months + p.open_invoices + p.open_requests + p.open_tasks;

/**
 * Archivieren statt löschen (AD-068) bzw. wiederherstellen (AD-049). Mit offenen Posten verlangt die Datenbank die Bestätigung
 * der Prüfliste; offene Anforderungen beim Creator werden dabei erledigt (0041).
 */
export async function archiviere(api: Api, dealIds: readonly string[], archiv: boolean, bestaetigt = false): Promise<number> {
  if (!dealIds.length) return 0;
  return Number(await api.rpc<number>("deal_archive", { p_deals: [...dealIds], p_archive: archiv, p_confirmed: bestaetigt }));
}

/** Nachricht an den Creator mit Verweis auf die Kooperation (Chat, AD-180 ff.). */
export async function sendeNachricht(api: Api, talentId: string, body: string, dealId: string): Promise<string> {
  return api.rpc<string>("chat_send", { p_talent: talentId, p_body: body.trim(), p_link_kind: "deal", p_link_id: dealId });
}

// ── Content: Einreichung, Prüfung, Veröffentlichung (Befund 12) ────────────

export interface PruefErgebnis {
  status: "approved" | "changes_requested";
  version: number;
  round?: number;
  rounds?: number;
  included?: number;
  over?: boolean;
  fee_cents?: number | null;
}

/** Einreichung erfassen (Version n+1): Dateien nur aus dem Chat des Creators; der Monat steht danach auf „Freigabe Content“. */
export async function reicheContentEin(api: Api, input: { periodId: string; caption: string; dateien: readonly string[]; auchKooperation: boolean; beitragId?: string | null }): Promise<{ submission_id: string; version: number }> {
  return api.rpc("content_submit", {
    p_period: input.periodId, p_caption: input.caption.trim() || null, p_attachments: [...input.dateien], p_also_deal: input.auchKooperation,
    // Einreichung für einen Beitrag (0068); ohne Beitrag wie bisher für den Monat
    ...(input.beitragId ? { p_deliverable: input.beitragId } : {}),
  });
}

interface ContentInput {
  deal: Pick<DealFull, "id" | "status">;
  periodId: string;
  month: string;
  talentId: string;
  feedback: string;
  auchKooperation: boolean;
  /** Offene Einreichung; ohne sie erfasst die Prüfung eine (Content kam per Chat oder Mail) */
  submissionId?: string | null;
  /** Prüfung eines Beitrags (0068): offene Einreichung dieses Beitrags, sonst eine neue für ihn */
  beitragId?: string | null;
  /** Bezeichnung des Beitrags für die Nachricht an den Creator („Reel · Instagram“) */
  beitragName?: string | null;
}

async function offeneEinreichung(api: Api, input: ContentInput): Promise<string> {
  if (input.submissionId) return input.submissionId;
  const open = await api.select<{ id: string }>("deal_content_submissions", {
    columns: "id", filters: input.beitragId ? [["deliverable_id", "eq", input.beitragId], ["status", "eq", "submitted"]]
      : [["period_id", "eq", input.periodId], ["status", "eq", "submitted"], ["deliverable_id", "is", null]],
  });
  if (open[0]) return open[0].id;
  return (await reicheContentEin(api, { periodId: input.periodId, caption: "", dateien: [], auchKooperation: input.auchKooperation, beitragId: input.beitragId ?? null })).submission_id;
}

/**
 * Content freigeben (content-freigabe.html): Der Monat ist danach „freigegeben“, veröffentlicht erst mit Datum und Link.
 * Feedback geht als Nachricht an den Creator.
 */
export async function contentFreigeben(api: Api, input: ContentInput): Promise<PruefErgebnis> {
  const id = await offeneEinreichung(api, input);
  const r = await api.rpc<PruefErgebnis>("content_review", { p_submission: id, p_approve: true, p_feedback: input.feedback.trim() || null, p_also_deal: input.auchKooperation });
  const text = input.feedback.trim();
  // Nachricht an den Creator: deutsch wie alle Nachrichten der Agentur, unabhängig von der Oberflächensprache
  const was = input.beitragName ? `Entwurf „${input.beitragName}“` : "Content";
  await sendeNachricht(api, input.talentId, inLang("de", () => `${was} für ${monthText(input.month)} ist freigegeben.${text ? `\n\n${text}` : ""}`), input.deal.id);
  return r;
}

/** Korrektur anfordern: zurück auf „In Produktion“, zählt als Korrekturschleife; das Feedback ist Pflicht und geht an den Creator. */
export async function korrekturAnfordern(api: Api, input: ContentInput): Promise<PruefErgebnis> {
  const text = input.feedback.trim();
  if (!text) throw new ApiError(t("Bitte beschreibe die gewünschte Änderung. Das Feedback geht als Nachricht an den Creator.", "Please describe the change you want. The feedback goes to the creator as a message."));
  const id = await offeneEinreichung(api, input);
  const r = await api.rpc<PruefErgebnis>("content_review", { p_submission: id, p_approve: false, p_feedback: text, p_also_deal: input.auchKooperation });
  await sendeNachricht(api, input.talentId, inLang("de", () => `Korrektur${input.beitragName ? ` zu „${input.beitragName}“` : ""} für ${monthText(input.month)}: ${text}`), input.deal.id);
  return r;
}

/** Veröffentlichung eintragen (Datum Pflicht, Beitrags-Link optional) oder mit leerem Datum zurücknehmen. */
export async function setzeVeroeffentlichung(api: Api, input: { periodId: string; datum: string | null; link: string | null; auchKooperation: boolean }): Promise<void> {
  await api.rpc("period_set_published", { p_period: input.periodId, p_published_on: input.datum || null, p_post_url: input.link?.trim() || null, p_also_deal: input.auchKooperation });
}

/** Zusätzliche Korrekturschleife als Zusatzposition des Monats (provisionspflichtig wie das Honorar). */
export async function erfasseKorrekturschleife(api: Api, input: { orgId: string; dealId: string; periodId: string; betragCents: number; beschreibung: string }): Promise<void> {
  await api.insert("deal_line_items", {
    org_id: input.orgId, deal_id: input.dealId, period_id: input.periodId, kind: "revision", description: input.beschreibung.trim(), amount_cents: input.betragCents, commissionable: true,
  });
}

// ── Verträge (Befund 14) ───────────────────────────────────────────────────

/**
 * Vertrag mit der Kooperation verknüpfen oder lösen (0047): auch versendete und unterschriebene Verträge, ein Vertrag auch für
 * mehrere Kooperationen (Rahmenvertrag). Ohne Hauptbezug wird die Kooperation der Hauptbezug. Die Datenbank protokolliert.
 */
/** Verknüpfen oder lösen; bei unterschriebenen Verträgen mit Grund (Beweiskette, 0078). */
export async function verknuepfeVertrag(api: Api, contractId: string, dealId: string, verknuepfen = true, grund?: string | null): Promise<{ linked: boolean; how: "main" | "link" | null }> {
  return api.rpc("contract_deal_link", { p_contract: contractId, p_deal: dealId, p_link: verknuepfen, p_reason: grund?.trim() || null });
}

// ── Kampagnen (Befund 22) ──────────────────────────────────────────────────

export interface KampagnenDaten {
  title: string;
  briefing: string | null;
  budget_cents: number | null;
  deadline: string | null;
  status: CampaignRow["status"];
}

export async function legeKampagneAn(api: Api, orgId: string, brandId: string, currency: string, d: KampagnenDaten): Promise<CampaignRow> {
  return one(await api.insert<CampaignRow>("campaigns", { org_id: orgId, brand_id: brandId, currency, ...d }), t("Kampagne", "Campaign"));
}
export async function speichereKampagne(api: Api, id: string, d: KampagnenDaten): Promise<CampaignRow> {
  return one(await api.update<CampaignRow>("campaigns", [["id", "eq", id]], { ...d }), t("Kampagne", "Campaign"));
}
/** Kooperationen einer Kampagne zuordnen oder herausnehmen (Marke und Währung prüft die Datenbank). */
export async function ordneKampagneZu(api: Api, dealIds: readonly string[], campaignId: string | null): Promise<void> {
  if (!dealIds.length) return;
  const rows = await api.update<DealFull>("deals", [["id", "in", [...dealIds]]], { campaign_id: campaignId });
  if (rows.length < dealIds.length) throw denied(t("Kooperation", "Deal"));
}

// ── Einstellungen der Kooperationen (Befunde 12, 17, 24, 27) ──────────────

export interface KoopEinstellungen {
  revenue_start: "status" | "contract_signed" | null;
  free_revisions: number | null;
  revision_fee_cents: number | null;
  /** Formate ohne Vorabfreigabe (0095, PRAXIS-R3-5) */
  deliverable_no_approval_formats?: string[] | null;
}
export async function speichereKoopEinstellungen(api: Api, orgId: string, patch: Partial<KoopEinstellungen>): Promise<void> {
  one(await api.update("org_settings", [["org_id", "eq", orgId]], { ...patch }), t("Einstellungen", "Settings"));
}
export async function ladeKoopEinstellungen(api: Api, orgId: string): Promise<{ settings: SettingsLite | null; platforms: CatalogEntry[]; itemKinds: CatalogEntry[]; raw: Partial<KoopEinstellungen> | null }> {
  const [cfg, kat, raw] = await Promise.all([
    ladeEinstellungen(api, orgId),
    ladeKataloge(api, orgId),
    quiet(api.select<KoopEinstellungen>("org_settings", { columns: "revenue_start, free_revisions, revision_fee_cents, deliverable_no_approval_formats", filters: byOrg(orgId) })).then((r) => r[0] ?? null),
  ]);
  return { settings: cfg.settings, ...kat, raw };
}

/** Katalogeintrag anlegen: Schlüssel aus der Bezeichnung (klein, ohne Umlaute), eindeutig je Agentur. */
export function katalogSchluessel(label: string): string {
  const base = label.trim().toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30);
  return base.length >= 2 ? base : `x_${base}`.slice(0, 30);
}
export async function legeKatalogEintragAn(api: Api, table: "org_deal_platforms" | "org_item_kinds", orgId: string, label: string, extra: { commissionable?: boolean } = {}): Promise<CatalogEntry> {
  const key = katalogSchluessel(label);
  return one(await api.insert<CatalogEntry>(table, { org_id: orgId, key, label_de: label.trim(), sort_order: 500, ...(table === "org_item_kinds" ? { commissionable: extra.commissionable ?? true } : {}) },
    { columns: table === "org_item_kinds" ? `${CATALOG_COLUMNS}, commissionable` : CATALOG_COLUMNS }), t("Katalogeintrag", "Catalogue entry"));
}
export async function aendereKatalogEintrag(api: Api, table: "org_deal_platforms" | "org_item_kinds", id: string, patch: Partial<Pick<CatalogEntry, "label_de" | "label_en" | "active" | "sort_order" | "commissionable">>): Promise<void> {
  one(await api.update(table, [["id", "eq", id]], patch, { columns: "id" }), t("Katalogeintrag", "Catalogue entry"));
}
